// src/components/PackageSuccessModal.js
import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import {
  CheckCircle,
  Inventory, // ✅ Use this instead of Package
  AccountBalance,
  EmojiEvents,
  ShoppingBag,
  ArrowForward,
} from "@mui/icons-material";

const PackageSuccessModal = ({
  open,
  onClose,
  onContinueShopping,
  packageData,
  orderData,
  primaryBlue,
}) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...glassStyles,
          bgcolor: "rgba(255, 255, 255, 0.95)",
          maxWidth: "600px",
          margin: "auto",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${primaryBlue[50]} 0%, ${primaryBlue[100]} 100%)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Background decoration */}
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: `linear-gradient(45deg, ${primaryBlue[200]}, ${primaryBlue[300]})`,
              opacity: 0.3,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
              opacity: 0.2,
            }}
          />

          {/* Content */}
          <Box sx={{ position: "relative", zIndex: 1, p: 4 }}>
            {/* Success Icon and Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
                  mb: 3,
                  boxShadow: "0 8px 32px rgba(76, 175, 80, 0.3)",
                }}
              >
                <CheckCircle sx={{ fontSize: 48, color: "white" }} />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#2e7d32",
                  mb: 1,
                }}
              >
                🎉 Order Placed Successfully!
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: primaryBlue[700],
                  fontWeight: 500,
                }}
              >
                Your package order has been submitted
              </Typography>
            </Box>

            {/* Order Details Card */}
            <Card
              sx={{
                mb: 3,
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Inventory
                    sx={{ color: primaryBlue[600], mr: 2, fontSize: 32 }}
                  />
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: primaryBlue[800] }}
                    >
                      {packageData?.name}
                    </Typography>
                    <Chip
                      label={packageData?.category}
                      size="small"
                      sx={{
                        bgcolor: primaryBlue[100],
                        color: primaryBlue[700],
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Order ID:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontFamily: "monospace" }}
                  >
                    {orderData?.orderId?.includes("CART-")
                      ? `CART${orderData.orderId.split("-")[1]?.slice(-6)}`
                      : orderData?.orderId || "Processing..."}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Package Price:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(packageData?.price || 0)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Total Amount:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#00BFFF" }}
                  >
                    {formatPrice(
                      orderData?.totalAmount || packageData?.price || 0
                    )}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={
                      orderData?.status === "confirmed"
                        ? "Payment confirmed"
                        : "Pending admin verification"
                    }
                    color={
                      orderData?.status === "confirmed" ? "success" : "info"
                    }
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Important Information */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                background: "rgba(0, 114, 206, 0.1)",
                border: "1px solid rgba(0, 114, 206, 0.3)",
                "& .MuiAlert-icon": {
                  color: primaryBlue[600],
                },
              }}
            >
              <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                <strong>What's Next?</strong>
                <br />
                {orderData?.status === "confirmed" ? (
                  <>
                    • Your payment was successful and your order is confirmed
                    <br />
                    • Referral points and membership benefits are applied
                    <br />
                  </>
                ) : (
                  <>
                    • We received your transaction ID and will verify your UPI
                    payment
                    <br />
                    • Membership and benefits apply after admin confirmation
                    <br />
                  </>
                )}
                • Check &quot;My Orders&quot; for status updates
              </Typography>
            </Alert>

            {/* First Package Purchase Benefits */}
            {!orderData?.userHadPackageBefore && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  background: "rgba(76, 175, 80, 0.1)",
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                  "& .MuiAlert-icon": {
                    color: "#00BFFF",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <EmojiEvents sx={{ color: "#00BFFF", mr: 1 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#2e7d32" }}
                  >
                    🎉 First Package Purchase Benefits!
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "#388e3c" }}>
                  • First-time cashback is credited when applicable
                  <br />
                  • Unlock referral Points eligibility
                  <br />• Access to exclusive Ultra Customer benefits
                </Typography>
              </Alert>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: "center",
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={onClose}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderColor: primaryBlue[300],
                  color: primaryBlue[600],
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: primaryBlue[500],
                    bgcolor: "rgba(0, 114, 206, 0.1)",
                  },
                }}
              >
                View My Orders
              </Button>

              <Button
                variant="contained"
                size="large"
                onClick={onContinueShopping}
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  boxShadow: `0 4px 20px ${primaryBlue[200]}`,
                  "&:hover": {
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 25px ${primaryBlue[300]}`,
                  },
                }}
              >
                <ShoppingBag sx={{ mr: 1 }} />
                Continue Shopping
              </Button>
            </Box>

            {/* Payment Confirmation Note */}
            <Box
              sx={{
                mt: 4,
                p: 3,
                background: "rgba(255, 255, 255, 0.7)",
                borderRadius: 2,
                border: `1px solid ${primaryBlue[200]}`,
                textAlign: "center",
              }}
            >
              <AccountBalance
                sx={{ color: primaryBlue[600], mb: 1, fontSize: 32 }}
              />
              <Typography
                variant="body1"
                sx={{ color: primaryBlue[700], fontWeight: 600, mb: 1 }}
              >
                Payment Submitted Successfully
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your payment was successful. Your order is confirmed and
                membership benefits are active.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PackageSuccessModal;
